import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Shape mínima de opción de select (compatible con SelectOption de ui-library).
 */
export interface ReasonsSelectOption {
  value: string;
  label: string;
}

/**
 * Catálogo de motivos para selects (cumplimiento / bloqueos).
 *
 * Endpoint:
 *   GET {apiUrl}/selects/reasons?countryId={countryId}
 *   → { success: true, data: [{ id, text }] }
 */
@Injectable({ providedIn: 'root' })
export class ReasonsSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private readonly cache = new Map<string, ReasonsSelectOption[]>();
  private readonly endpointUrl = '/selects/reasons';

  getOptions(
    countryId: string | null | undefined,
  ): Observable<ReasonsSelectOption[]> {
    if (!countryId) {
      return of([]);
    }

    const cached = this.cache.get(countryId);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(
        `${this.apiConfig.apiUrl}${this.endpointUrl}?countryId=${encodeURIComponent(countryId)}`,
        {
          observe: 'response',
          responseType: 'text',
        },
      )
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(countryId, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): ReasonsSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || '',
      }));
    } catch {
      return [];
    }
  }
}
